export const comparisonModels = [
  {
    id: 'spread-only',
    name: 'Spread-Only Account',
    broker: 'Standard / Market Maker',
    icon: 'ri-exchange-dollar-line',
    spreadLabel: '1.2 – 2.0 pips',
    commissionLabel: 'None',
    pricingModel: 'Markup baked into the bid-ask spread',
    desc: 'One visible cost — no separate line items. You pay a wider spread and the broker earns the difference. Simple, predictable, but rarely the cheapest option for active traders.',
    pros: ['Simple pricing — one number to watch', 'No surprise commission bills at month-end', 'Lower minimum deposits usually required'],
    cons: ['Higher effective cost per trade', 'Broker is your counterparty (conflict of interest)', 'Spread can widen unpredictably during news'],
    bestFor: 'Beginners and low-volume traders (< 20 lots/month)',
    colorRole: 'primary',
    costPerLotTotal: 14.00,
  },
  {
    id: 'ecn-commission',
    name: 'ECN / Commission Account',
    broker: 'ECN / Pro / Raw',
    icon: 'ri-bank-card-line',
    spreadLabel: '0.1 – 0.3 pips',
    commissionLabel: '$3.50 – $7.00 per side',
    pricingModel: 'Raw interbank spread + transparent commission',
    desc: 'Raw market pricing with a separate commission. Two visible costs but the total is often lower than spread-only accounts. The math is transparent — you know exactly what you pay.',
    pros: ['True market execution with no dealer intervention', 'Total cost often 25–40% lower for active traders', 'Full transparency — you see every dollar'],
    cons: ['Commission adds up fast for scalpers', 'Higher minimum deposits usually required', 'Two numbers to track instead of one'],
    bestFor: 'Active traders and scalpers (> 20 lots/month)',
    colorRole: 'accent',
    costPerLotTotal: 8.50,
  },
] as const;

export const sideBySideComparison = [
  {
    scenario: 'EURUSD — 1 standard lot during London session',
    label: 'Typical trade',
    spreadOnly: { spreadPips: 1.2, spreadCost: 12.00, commission: 0, total: 12.00 },
    commissionOnly: { spreadPips: 0.2, spreadCost: 2.00, commission: 7.00, total: 9.00 },
    winner: 'commission',
    savings: '$3.00 (25% less)',
    note: 'Commission account saves $3 per lot on the world\'s most liquid pair.',
  },
  {
    scenario: 'GBPJPY — 1 standard lot during Asian session',
    label: 'Cross pair in thinner liquidity',
    spreadOnly: { spreadPips: 2.5, spreadCost: 25.00, commission: 0, total: 25.00 },
    commissionOnly: { spreadPips: 1.0, spreadCost: 10.00, commission: 9.00, total: 19.00 },
    winner: 'commission',
    savings: '$6.00 (24% less)',
    note: 'The gap widens on less liquid pairs — commission account pulls further ahead.',
  },
  {
    scenario: 'XAUUSD — 1 standard lot (100 oz) during news',
    label: 'Gold during volatility',
    spreadOnly: { spreadPips: 35.0, spreadCost: 35.00, commission: 0, total: 35.00 },
    commissionOnly: { spreadPips: 28.0, spreadCost: 28.00, commission: 7.00, total: 35.00 },
    winner: 'tie',
    savings: 'Same total cost',
    note: 'During extreme volatility, commission savings shrink. Both models converge.',
  },
  {
    scenario: 'EURUSD — 0.01 lot (micro) during London',
    label: 'Smallest trade size',
    spreadOnly: { spreadPips: 1.2, spreadCost: 0.12, commission: 0, total: 0.12 },
    commissionOnly: { spreadPips: 0.2, spreadCost: 0.02, commission: 0.07, total: 0.09 },
    winner: 'commission',
    savings: '$0.03 (25% less)',
    note: 'Even at micro lots, commission account wins — the math scales down proportionally.',
  },
] as const;

export const breakEvenScenarios = [
  {
    label: 'Scalper',
    tradesPerMonth: 300,
    lotSize: 0.1,
    spreadOnlySpread: 1.2,
    ecnSpread: 0.2,
    ecnCommissionPerSide: 3.50,
    description: '15 round-turns per day at 0.1 lots',
  },
  {
    label: 'Day Trader',
    tradesPerMonth: 100,
    lotSize: 0.5,
    spreadOnlySpread: 1.2,
    ecnSpread: 0.2,
    ecnCommissionPerSide: 3.50,
    description: '5 round-turns per day at 0.5 lots',
  },
  {
    label: 'Swing Trader',
    tradesPerMonth: 20,
    lotSize: 2.0,
    spreadOnlySpread: 1.2,
    ecnSpread: 0.2,
    ecnCommissionPerSide: 3.50,
    description: '1 trade per day at 2.0 lots',
  },
  {
    label: 'Position Trader',
    tradesPerMonth: 6,
    lotSize: 5.0,
    spreadOnlySpread: 1.2,
    ecnSpread: 0.2,
    ecnCommissionPerSide: 3.50,
    description: '~1.5 trades per week at 5.0 lots',
  },
] as const;

export const realTraderScenarios = [
  {
    name: 'Marco — EURUSD Scalper',
    tradesPerDay: 20,
    daysPerMonth: 20,
    lotsPerTrade: 0.1,
    pipValue: 1.0,
    spreadOnly: { spreadPips: 1.2, commission: 0, monthlyCost: 480.00, annualCost: 5760, note: '480 trades/month × 0.1 lots × 1.2 pips × $1/pip' },
    ecn: { spreadPips: 0.2, commissionPerSide: 3.50, monthlyCost: 356.00, annualCost: 4272, note: '480 trades × 0.1 lots × (0.2 pips × $1 + $7 RT)' },
    verdict: 'ECN saves $1,488/year — 25.8% cheaper',
  },
  {
    name: 'Sarah — GBPUSD Day Trader',
    tradesPerDay: 4,
    daysPerMonth: 20,
    lotsPerTrade: 1.0,
    pipValue: 10.0,
    spreadOnly: { spreadPips: 1.5, commission: 0, monthlyCost: 1200.00, annualCost: 14400, note: '80 trades/month × 1.0 lot × 1.5 pips × $10/pip' },
    ecn: { spreadPips: 0.4, commissionPerSide: 3.50, monthlyCost: 880.00, annualCost: 10560, note: '80 trades × 1.0 lot × (0.4 pips × $10 + $7 RT)' },
    verdict: 'ECN saves $3,840/year — 26.7% cheaper',
  },
  {
    name: 'Kenji — USDJPY Swing Trader',
    tradesPerDay: 1,
    daysPerMonth: 20,
    lotsPerTrade: 3.0,
    pipValue: 9.0,
    spreadOnly: { spreadPips: 1.0, commission: 0, monthlyCost: 540.00, annualCost: 6480, note: '20 trades/month × 3.0 lots × 1.0 pip × $9/pip' },
    ecn: { spreadPips: 0.2, commissionPerSide: 3.50, monthlyCost: 528.00, annualCost: 6336, note: '20 trades × 3.0 lots × (0.2 pips × $9 + $7 RT)' },
    verdict: 'ECN saves $144/year — 2.2% cheaper (negligible for swing traders)',
  },
] as const;

// ── Execution Models Section ──
export const executionSteps = [
  {
    step: '01',
    label: 'Trader sends order',
    spreadOnly: 'Click buy on EURUSD — your order arrives at the broker\'s dealing desk.',
    ecn: 'Click buy on EURUSD — your order arrives at the broker\'s aggregation engine.',
  },
  {
    step: '02',
    label: 'Broker handles the order',
    spreadOnly: 'The broker acts as the counterparty. They fill your order at their own price (bid/ask includes their markup).',
    ecn: 'The broker routes your order to multiple liquidity providers (banks, ECNs, other brokers) and finds the best available price.',
  },
  {
    step: '03',
    label: 'Execution happens',
    spreadOnly: 'You get filled at 1.2000 / 1.2012 — the 1.2-pip gap is how the broker earns. No separate line item.',
    ecn: 'You get filled at 1.2000 / 1.2002 — raw interbank pricing. A $7 commission line appears on your statement.',
  },
  {
    step: '04',
    label: 'After the trade',
    spreadOnly: 'You paid $12 to enter. The cost is invisible — it lives inside the spread, so you never see a bill.',
    ecn: 'You paid $2 (spread) + $7 (commission) = $9 total. Both line items are transparent on your trade ticket.',
  },
] as const;

export const executionTakeaways = [
  {
    icon: 'ri-eye-off-line',
    title: 'Spread-only hides the cost',
    desc: 'You paid for the trade — you just never see a separate bill. The spread markup is priced into every fill.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'ECN shows every dollar',
    desc: 'Raw spread + visible commission — two numbers, both transparent. You know exactly what you paid and to whom.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Same trade, different plumbing',
    desc: 'The underlying market is the same. What changes is the broker\'s role in the transaction — counterparty vs intermediary.',
  },
] as const;

// ── When Each Wins Section ──
export const spreadOnlyWinsWhen = [
  {
    condition: 'You trade under 7 lots per month',
    detail: 'The cost difference between models is under $10/month. At this volume, platform quality, execution speed, and customer support matter more than which pricing model you pick.',
  },
  {
    condition: 'You trade exotic currency pairs',
    detail: 'Wide spreads dominate regardless of pricing model. On pairs like USDTRY or USDMXN, the raw interbank spread advantage of ECN nearly disappears — both models converge at high spreads.',
  },
  {
    condition: 'You are a position trader holding for weeks or months',
    detail: 'Swap costs dominate your total trading costs at this horizon. The spread-vs-commission debate becomes secondary — focus on swap rates and rollover policies instead.',
  },
  {
    condition: 'Simplicity matters more than squeezing out every dollar',
    detail: 'One number to watch, no separate commission line items on your statement. For some traders, psychological comfort and clean accounting outweigh a $20–50/month saving. The mathematically optimal choice is not always the personally optimal one.',
  },
] as const;

export const ecnWinsWhen = [
  {
    condition: 'You trade 20 or more lots per month',
    detail: 'At this volume, ECN saves you 25–40% on total trading costs. At 100 lots/month, that is roughly $300/month — real money. The spread-only markup compounds into thousands per year.',
  },
  {
    condition: 'You scalp or day trade with high frequency',
    detail: 'For scalpers, commission IS the biggest line item — often 3–4× larger than the spread component. Every dollar of commission rate matters: cutting from $7/RT to $5/RT saves roughly $280/month on the exact same trading.',
  },
  {
    condition: 'You primarily trade major currency pairs',
    detail: 'Raw interbank spreads on EURUSD, USDJPY, and GBPUSD are genuinely tight — 0.1 to 0.3 pips in normal conditions. The ECN advantage over spread-only is at its maximum when the underlying market is the most liquid.',
  },
  {
    condition: 'You want to see exactly what you paid on every trade',
    detail: 'Raw spread + visible commission — two line items, both transparent. No hidden markup baked into the bid-ask. You know exactly what went to the market and what went to the broker.',
  },
] as const;

export const verdictQuickRef = [
  {
    profile: 'Scalper',
    volume: '300+ trades/month',
    recommendation: 'ECN',
    strength: 'Strong',
    note: 'Commission dominates total cost. Even a $1/RT rate difference is material at this frequency.',
  },
  {
    profile: 'Day Trader',
    volume: '80–100 trades/month',
    recommendation: 'ECN',
    strength: 'Strong',
    note: 'Volume is high enough that the 25–40% spread savings clearly outweigh the commission.',
  },
  {
    profile: 'Swing Trader',
    volume: '15–30 trades/month',
    recommendation: 'Either',
    strength: 'Neutral',
    note: 'The cost difference is modest. Prioritize swap rates, platform quality, and execution reliability.',
  },
  {
    profile: 'Position Trader',
    volume: '5–10 trades/month',
    recommendation: 'Spread-Only',
    strength: 'Mild',
    note: 'At this frequency, simplicity wins. Focus on swap optimization — not spread vs commission.',
  },
] as const;

// ── Spread Dynamics Section ──
export const spreadDynamicsScenarios = [
  {
    market: 'Calm London session (10:00 GMT)',
    liquidity: 'High — all major players active',
    icon: 'ri-sun-line',
    spreadOnly: { spreadPips: 1.2, costPerLot: 12.00 },
    ecn: { spreadPips: 0.2, commission: 7.00, total: 9.00 },
    advantage: 'ECN saves $3.00 (25% less)',
    note: 'In normal conditions, the ECN raw spread advantage is at its fullest.',
  },
  {
    market: 'NFP release (first 60 seconds)',
    liquidity: 'Extreme volatility — liquidity providers pull back',
    icon: 'ri-flashlight-line',
    spreadOnly: { spreadPips: 8.0, costPerLot: 80.00 },
    ecn: { spreadPips: 5.5, commission: 7.00, total: 62.00 },
    advantage: 'ECN saves $18.00 (23% less)',
    note: 'Both widen dramatically, but the gap shrinks proportionally. ECN still wins in absolute dollars.',
  },
  {
    market: 'Asian session on GBPJPY (02:00 GMT)',
    liquidity: 'Thin — fewer market participants',
    icon: 'ri-moon-line',
    spreadOnly: { spreadPips: 3.5, costPerLot: 35.00 },
    ecn: { spreadPips: 2.2, commission: 9.00, total: 31.00 },
    advantage: 'ECN saves $4.00 (11% less)',
    note: 'On less liquid pairs, the percentage advantage narrows but ECN remains cheaper in absolute terms.',
  },
  {
    market: 'Weekend gap reopen (Sunday 22:00 GMT)',
    liquidity: 'Very thin — only early Asian and Wellington',
    icon: 'ri-alarm-warning-line',
    spreadOnly: { spreadPips: 6.0, costPerLot: 60.00 },
    ecn: { spreadPips: 5.0, commission: 7.00, total: 57.00 },
    advantage: 'ECN saves $3.00 (5% less)',
    note: 'During gaps and reopenings, the models nearly converge — the raw spread advantage almost disappears.',
  },
] as const;

// ── FAQs ──
export const spreadVsCommissionFAQs = [
  {
    question: 'Which is cheaper — spread-only or commission account?',
    answer: 'For most active traders doing 20+ lots per month, the commission (ECN) account is cheaper by 25–40%. For very low-volume traders doing under 20 lots per month, the difference is small — sometimes under $20/month. The break-even depends on your specific trading volume, pair selection, and commission rate. Always run the math: Total Cost = (Spread in pips × Pip Value) + (Commission per side × 2) for both account types and compare.',
    category: 'The Core Question',
  },
  {
    question: 'Why would anyone choose a spread-only account if commission accounts are cheaper?',
    answer: 'Three reasons: (1) Simplicity — one number to watch is genuinely easier for beginners, (2) Minimum deposit — ECN accounts often require $500–$2,000 vs $50–$100 for standard accounts, (3) Psychological comfort — no separate commission bill at month-end. The behavioral aspect matters: some traders trade worse when they see commission deductions on every trade. The mathematically optimal choice is not always the psychologically optimal one.',
    category: 'The Core Question',
  },
  {
    question: 'At what volume does commission account become clearly cheaper?',
    answer: 'For EURUSD with typical rates (1.2 pip spread-only vs 0.2 pip + $7 RT), the break-even is around 7 lots per month. Below 7 lots, the difference is under $10/month — negligible. At 20 lots/month, the ECN account saves about $60/month. At 100 lots/month, savings jump to $300/month. The curve is linear: each additional lot saves you about $3 on major pairs. If you trade 50+ lots monthly, you are leaving real money on the table with a spread-only account.',
    category: 'Break-Even Analysis',
  },
  {
    question: 'Does the pair I trade change which account is better?',
    answer: 'Yes — significantly. On EURUSD (liquid, tight spreads), the ECN advantage is clear: 1.2 vs 0.2 pip spread difference. On exotic pairs like USDTRY (wide spreads regardless), the spread difference between account types shrinks because even raw interbank spreads are wide. If your primary pairs are exotic or minor, the commission account advantage shrinks or may disappear entirely. Run the math for your actual traded pairs, not just EURUSD.',
    category: 'Break-Even Analysis',
  },
  {
    question: 'How do I verify a broker is giving me true ECN pricing and not just calling it ECN?',
    answer: 'Three checks: (1) Compare the spread to a reputable independent source like the CME futures spread for the same currency — if your "ECN" spread is consistently wider, you are getting a markup. (2) Check whether your broker allows trading during news events without requotes — true ECN passes orders straight through. (3) Look for a "Depth of Market" (DOM) window showing multiple liquidity provider quotes — this is a strong indicator of genuine ECN. If the broker hides the order book, be suspicious.',
    category: 'Broker Verification',
  },
  {
    question: 'Can I switch between account types at the same broker?',
    answer: 'Most brokers allow it, but there is often friction. You typically need to open a new account — you cannot flip a switch on an existing one. The process usually involves: KYC verification on the new account (even if already done), funding the new account separately, and manually transferring positions or closing and reopening. Some brokers let you link accounts under one login. Ask before opening: "Can I test both your standard and ECN accounts with a single login?"',
    category: 'Broker Verification',
  },
  {
    question: 'Do commission rates stay the same for all currency pairs?',
    answer: 'No. Most ECN brokers charge different commission rates based on pair category. A typical structure: Major pairs (EURUSD, USDJPY) = $3.50/side, Minor pairs (EURGBP, AUDCAD) = $5.00/side, Exotic pairs (USDTRY, USDMXN) = $7.00–$10.00/side. CFD indices and commodities also have separate structures. Always check the full contract specifications before calculating — do not assume the EURUSD rate applies to every instrument you trade.',
    category: 'Commission Details',
  },
  {
    question: 'What happens to commission during high volatility — does it change?',
    answer: 'Commission per lot stays fixed — that is the point. A $3.50/side commission is $3.50 regardless of whether EURUSD is calm or surging on NFP. What changes is the spread component: raw ECN spreads can widen from 0.2 to 5+ pips during extreme events. So your total cost during news on an ECN account = wide spread + fixed commission. On a spread-only account, the spread widens even more because the broker adds their markup on top of the already-wide raw spread.',
    category: 'Commission Details',
  },
  {
    question: 'How does commission affect scalping specifically?',
    answer: 'Commission is the scalper\'s biggest expense — by a wide margin. A scalper doing 20 round-turns daily at 0.1 lots with $3.50/side pays $14/day in commission alone. Spread cost at 0.2 pips is negligible: 20 trades × 0.1 lots × 0.2 pips × $10 = $4/day. Commission is 3.5× larger than spread. This is why scalpers obsess over commission rates: cutting from $7/RT to $5/RT saves $14/day — $280/month — on the exact same trading. Every dollar of commission matters disproportionately for high-frequency styles.',
    category: 'Trading Style Impact',
  },
  {
    question: 'For swing traders — does account type even matter?',
    answer: 'Barely. A swing trader doing 20 trades/month at 2.0 lots pays $140 in ECN commission vs $480 in spread-only spread cost — a $340/month difference. But swap costs for a swing trader holding 5 positions for 10 days each at 0.8 pips/day = 5 × 2 lots × 8 pips × $10 = $800/month. Swap is the dominant cost, not spread or commission. For swing and position traders, focus on swap rates and rollover policies — the spread-vs-commission debate is secondary.',
    category: 'Trading Style Impact',
  },
  {
    question: 'Are there brokers that offer both models on the same account?',
    answer: 'Rarely on the same account, but many brokers offer both account types under one login. You can have a standard account for casual trading and an ECN account for high-volume days. Some brokers offer "hybrid" accounts with reduced spread + reduced commission — a middle ground that splits the difference. These are not the cheapest for any specific trading style, but they offer flexibility for traders who switch between scalping and swing trading.',
    category: 'Broker Verification',
  },
  {
    question: 'Does the regulatory jurisdiction change commission rules?',
    answer: 'Yes. ESMA-regulated brokers (EU) have leverage caps (30:1) which indirectly affect commission economics — lower leverage means more capital tied up, reducing the number of lots you can trade, which reduces total commission volume. Australian (ASIC) and offshore-regulated brokers typically allow higher leverage (up to 500:1), enabling higher lot volumes and making the commission vs spread comparison more impactful. Regulatory environment directly shapes your cost structure by limiting or enabling volume.',
    category: 'Commission Details',
  },
] as const;

export const relatedSpreadVsCommissionGuides = [
  {
    title: 'Forex Spread Explained',
    href: '/forex-spread/',
    desc: 'The bid-ask gap hits you the second you enter a trade. Understand what determines its size and how to trade when spreads are tightest.',
    icon: 'ri-arrow-left-right-line',
  },
  {
    title: 'Forex Commission Explained',
    href: '/forex-commission/',
    desc: 'ECN commissions look like an extra cost but often save you money. Here\'s when paying a flat fee beats a wider spread.',
    icon: 'ri-bank-card-line',
  },
  {
    title: 'Forex Trading Costs by Strategy',
    href: '/forex-trading-costs-by-strategy/',
    desc: 'Your trading style determines which cost hurts most. Scalpers fight commissions, swing traders battle swaps — find your biggest leak.',
    icon: 'ri-line-chart-line',
  },
  {
    title: 'Calculate Forex Trading Costs',
    href: '/calculate-forex-trading-costs/',
    desc: 'Three costs, one formula. Add spread, commission, and swap together to see the real price of any trade before you take it.',
    icon: 'ri-calculator-line',
  },
] as const;